import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, inject } from 'vue';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useAppStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { SnowplowContext, SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { OfferPresentationType } from '@/interfaces/titles';
import { PresentationType } from '@/@types/graphql-types';
import { OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import { OfferLocation } from '@/helpers/offer-helper';
import { useBuyboxLoggedInExp } from '../experiments/BuyboxLoggedInExp/composables/useBuyboxLoggedInExp';
import { useAmzAssociatesImpactExp } from '../experiments/AmazonAssoicatesImpactExp';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

interface BuyBoxBundleProps {
	title: TitleDetail;
	offer: BuyboxRowOffer;
	isBannerBuyBox: boolean;
	additionalContexts?: SnowplowContext[];
	episodeBuyBoxPresentationType?: OfferPresentationType | null;
	isEpisodeBuybox?: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxBundle',
  props: {
    title: null,
    offer: null,
    isBannerBuyBox: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    episodeBuyBoxPresentationType: null,
    isEpisodeBuybox: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, offer: BuyboxRowOffer, isBannerBuyBox: boolean, additionalContexts: SnowplowContext[], episodeBuyBoxPresentationType?: OfferPresentationType | null, isEpisodeBuybox?: boolean };



const { country } = useLanguageStore();
const { buyboxPresentationType: regularBuyboxPresentationType } = useUserStore();
const { xServeTrack } = useAppStore();
const { setAmzAssImpExpTag } = useAmzAssociatesImpactExp();
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const offerEntityId = computed(() => episodeEntityId ?? props.title.id);

const buyboxPresentationType = computed(() => {
	return props.isEpisodeBuybox ? props.episodeBuyBoxPresentationType : regularBuyboxPresentationType.value;
});

const bundleLabelSuffix = computed(() => {
	switch (buyboxPresentationType.value) {
		case null:
		case OfferPresentationType._4K:
			return '4k';
		case OfferPresentationType.HD:
			return 'HD';
		default:
			return '';
	}
});

const bundlePresentationType = computed(() => {
	switch (buyboxPresentationType.value) {
		case null:
		case OfferPresentationType._4K:
			return PresentationType['4K'];
		case OfferPresentationType.HD:
			return PresentationType['Hd'];
		default:
			return props.offer.presentationType;
	}
});

const packageContext = computed(
	() =>
		new SnowplowPackageContext(
			props.offer.bundleId ?? props.offer.package.packageId,
			props.offer.bundleTechnicalName ?? props.offer.package.technicalName,
			'bundle'
		)
);

const bundleOfferUrl = computed(() => {
	const url = new ClickoutUrl(() => ({ ...props.offer, presentationType: bundlePresentationType.value }))
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.addContexts(packageContext)
		.addContexts(titleContext)
		.setForTracking(
			'offerLocation',
			props.isBannerBuyBox ? OfferLocation.BANNER_BUYBOX : OfferLocation.REGULAR_BUYBOX
		)
		.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId.value)
		.setCanary(xServeTrack.value);

	// Bypass UCT for US users on bundle amazonstarzamazonhbomax
	if (props.offer.bundleTechnicalName === 'amazonstarzamazonhbomax') {
		url.set('uct_ct', 'bypass');
	}
	return setAmzAssImpExpTag(url);
});

// BUYBOX_LOGGED_IN_EXP
const { trackBuyboxLoggedInExp } = useBuyboxLoggedInExp();
function trackClickout() {
	trackBuyboxLoggedInExp({
		action: 'click',
		label: `clickout_buybox`,
	});
}
//BUYBOX_LOGGED_IN_EXP

return { __sfc: true,props, country, regularBuyboxPresentationType, xServeTrack, setAmzAssImpExpTag, titleContext, titleSourceId, episodeEntityId, offerEntityId, buyboxPresentationType, bundleLabelSuffix, bundlePresentationType, packageContext, bundleOfferUrl, trackBuyboxLoggedInExp, trackClickout, ProviderIcon }
}

})